import { jsonapiClient } from '@/api/client'
import type { paths } from '@/api/schema'
import { useApiErrorHandler } from '@/composables/useApiErrorHandler'
import { usePagination } from '@/composables/usePagination'
import { toast } from 'vue-sonner'
import { useI18n } from 'vue-i18n'
import type { Resource } from '@/api/resources'

type ServiceLocationsQueryParams = paths['/api/service_locations']['get']['parameters']['query']
type ServiceLocationAttrs = {
  name?: string | null
  address_line1?: string | null
  city?: string | null
  province?: string | null
  postal_code?: string | null
  country?: string | null
  notes?: string | null
  // From the address lookup (design 170): sent WITH the address, the server
  // stamps status + sector in the same request and skips the async job.
  latitude?: number
  longitude?: number
}

type PostRelationships = paths['/api/service_locations']['post']['requestBody']['content']['application/vnd.api+json']['data']['relationships']
export type ServiceLocationOwner =
  | NonNullable<PostRelationships['contact']>['data']
  | NonNullable<PostRelationships['company']>['data']

/**
 * The two INDEPENDENT customer slots: `company` = who pays, `contact` = who is on
 * site (and the only SMS target). A B2B address carries both, which is why this is
 * a pair of optional ids and not the `ServiceLocationOwner` union it replaced —
 * that union could only ever express ONE slot, while the server has accepted both
 * in a single create since §98 and 422s only when both are blank.
 */
export type ServiceLocationSlots = { contactId?: string | null; companyId?: string | null }

export function useServiceLocations() {
  const { t } = useI18n()
  const { handleError } = useApiErrorHandler()

  const pagination = usePagination<Resource<'service_location'>, ServiceLocationsQueryParams>(async (params) => {
    try {
      const response = await jsonapiClient.get('/api/service_locations', { params })
      return {
        data: response.data.data,
        meta: response.data.meta
      }
    } catch (error) {
      handleError(error)
      return null
    }
  })

  const fetchServiceLocations = async (owner?: ServiceLocationOwner) => {
    const filter = owner
      ? owner.type === 'contact'
        ? { contact_ids: [owner.id] }
        : { company_ids: [owner.id] }
      : undefined
    await pagination.fetchPage({ filter })
  }

  // The CustomerPicker's fetch: `q` matches customer names AND the address (the
  // second call's spelling — « j'appelle pour le 88 Cartier »), and never
  // surfaces a location nobody owns — `q` only filters when present, so
  // `with_customer` is what keeps the no-term open pickable.
  const searchByCustomer = async (q?: string) => {
    await pagination.fetchPage({
      q: q?.trim() || undefined,
      filter: { with_customer: true },
    })
  }

  // The address book (« Adresses » index): every location, newest first — NO
  // `with_customer`, because a legacy ownerless row must be findable here (it
  // renders « Aucun client »), just never in the intake picker.
  const fetchDirectory = async (q?: string, page?: number) => {
    await pagination.fetchPage({
      q: q?.trim() || undefined,
      sort: ['-created_at'],
      page: { number: page ?? 1 },
    })
  }

  const loadMoreServiceLocations = async () => {
    await pagination.loadMore()
  }

  // Label an id we were handed (a prefill) without waiting for a list fetch —
  // otherwise a preselected customer reads as an empty field until the dropdown
  // is opened. Mirrors useContacts#fetchContact.
  const fetchServiceLocation = async (id: string) => {
    try {
      const response = await jsonapiClient.get('/api/service_locations/{id}', { path: { id } })
      return response.data.data
    } catch (error) {
      handleError(error)
      return null
    }
  }

  // `silent`: see useContacts#create — quick-capture's one « Créer » must not
  // stack a location toast under the job's.
  const create = async (
    slots: ServiceLocationSlots,
    attrs: ServiceLocationAttrs,
    options?: { silent?: boolean }
  ) => {
    try {
      // Each slot is sent only when it has a value: an ABSENT key leaves that slot
      // alone server-side (the CustomerSlots tri-state), so a company-only address
      // must not ship `contact: { data: null }`. At least one is required — the
      // server 422s on both blank, and the caller's schema blocks it earlier.
      const relationships = {
        ...(slots.contactId ? { contact: { data: { type: 'contact' as const, id: slots.contactId } } } : {}),
        ...(slots.companyId ? { company: { data: { type: 'company' as const, id: slots.companyId } } } : {}),
      }

      const response = await jsonapiClient.post('/api/service_locations', {
        data: {
          type: 'service_location',
          attributes: attrs,
          relationships,
        }
      })
      const created = response.data.data
      pagination.prependItem(created)
      if (!options?.silent) toast.success(t('serviceLocations.createSuccess'))
      return created
    } catch (error) {
      handleError(error)
      return null
    }
  }

  /**
   * `slots` is TRI-STATE per slot, mirroring Api::Concerns::CustomerSlots: a key
   * left OUT leaves that slot untouched, `null` clears it, an id sets it. This is
   * why the parameter is optional rather than defaulted — an edit that only
   * changes the street must not silently restate (or wipe) who owns the address.
   */
  // `silent`: the dedup repoint ([Utiliser ce lieu]) rides inside quick-capture's
  // one « Créer » — the form toasts the job, once.
  const update = async (
    id: string,
    attrs: ServiceLocationAttrs,
    slots?: ServiceLocationSlots,
    options?: { silent?: boolean }
  ) => {
    try {
      const relationships = {
        ...(slots && 'contactId' in slots
          ? { contact: { data: slots.contactId ? { type: 'contact' as const, id: slots.contactId } : null } }
          : {}),
        ...(slots && 'companyId' in slots
          ? { company: { data: slots.companyId ? { type: 'company' as const, id: slots.companyId } : null } }
          : {}),
      }

      const response = await jsonapiClient.patch('/api/service_locations/{id}', {
        data: {
          id,
          type: 'service_location',
          attributes: attrs,
          ...(Object.keys(relationships).length ? { relationships } : {}),
        }
      }, { path: { id } })
      const updated = response.data.data
      pagination.replaceItem(id, updated)
      if (!options?.silent) toast.success(t('serviceLocations.updateSuccess'))
      return updated
    } catch (error) {
      handleError(error)
      return null
    }
  }

  const remove = async (id: string) => {
    try {
      await jsonapiClient.delete('/api/service_locations/{id}', { path: { id } })
      pagination.removeItem(id)
      toast.success(t('serviceLocations.deleteSuccess'))
      return true
    } catch (error) {
      handleError(error)
      return false
    }
  }

  return {
    serviceLocations: pagination.data,
    // The directory's sideloaded owners (contact / company ride the server's
    // default includes) — the address book draws their discs from here.
    included: pagination.included,
    loading: pagination.loading,
    meta: pagination.meta,
    hasMore: pagination.hasMore,
    fetchServiceLocations,
    searchByCustomer,
    fetchDirectory,
    loadMoreServiceLocations,
    fetchServiceLocation,
    create,
    update,
    remove
  }
}
